import React from 'react';
import { Trophy, RotateCcw, Home, Github, Linkedin, Mail, ExternalLink } from 'lucide-react';
import { portfolioData } from '../../data/portfolioData';
import confetti from 'canvas-confetti';

export const GameComplete = ({ totalCoins, itemsUnlocked, onRestart, onMainMenu }) => {
  React.useEffect(() => {
    // Celebration confetti
    const duration = 3000;
    const end = Date.now() + duration;
    
    const frame = () => {
      confetti({
        particleCount: 3,
        angle: 60,
        spread: 55,
        origin: { x: 0 },
        colors: ['#38bdf8', '#a855f7', '#facc15']
      });
      confetti({
        particleCount: 3,
        angle: 120,
        spread: 55,
        origin: { x: 1 },
        colors: ['#38bdf8', '#a855f7', '#facc15']
      });
      
      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    };
    
    frame();
  }, []);

  return (
    <div className="min-h-screen retro-ui flex flex-col items-center justify-center p-4 relative overflow-hidden" style={{ background: 'linear-gradient(to bottom, #7bcaff 0%, #d8f7ff 50%, #fff0a1 100%)' }} data-testid="game-complete">
      {/* Stars background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(50)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-white rounded-full animate-pulse"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 2}s`,
              animationDuration: `${1 + Math.random() * 2}s`
            }}
          />
        ))}
      </div>
      
      {/* Content */}
      <div className="relative z-10 text-center max-w-lg w-full">
        {/* Trophy */}
        <div className="w-32 h-32 mx-auto bg-gradient-to-br from-yellow-400 to-amber-500 rounded-full flex items-center justify-center shadow-2xl mb-6 animate-bounce">
          <Trophy className="w-16 h-16 text-white" />
        </div>
        
        {/* Title */}
        <h1 className="text-4xl md:text-5xl font-bold text-white drop-shadow-lg mb-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
          ADVENTURE
        </h1>
        <h2 className="text-3xl md:text-4xl font-bold text-yellow-300 drop-shadow-lg mb-6" style={{ fontFamily: 'Fredoka, sans-serif' }}>
          COMPLETE!
        </h2>
        
        {/* Stats */}
        <div className="bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 p-6 mb-6">
          <h3 className="text-white/80 text-lg mb-4">Your Journey Stats</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white/10 rounded-xl p-4">
              <p className="text-3xl font-bold text-yellow-400">{totalCoins}</p>
              <p className="text-white/70 text-sm">Coins Collected</p>
            </div>
            <div className="bg-white/10 rounded-xl p-4">
              <p className="text-3xl font-bold text-purple-300">{itemsUnlocked}</p>
              <p className="text-white/70 text-sm">Items Unlocked</p>
            </div>
          </div>
          
          <div className="mt-4 p-4 bg-white/10 rounded-xl">
            <p className="text-white font-semibold">You've discovered everything about</p>
            <p className="text-2xl font-bold text-yellow-300 mt-1">{portfolioData.personal.name}</p>
          </div>
        </div>
        
        {/* Contact CTA */}
        <div className="bg-white rounded-2xl shadow-2xl p-6 mb-6">
          <h3 className="text-xl font-bold text-slate-800 mb-2">Ready to Connect?</h3>
          <p className="text-slate-600 mb-4">Let's build something amazing together!</p>
          
          <div className="flex justify-center gap-4 mb-4">
            <a 
              href={portfolioData.personal.github}
              target="_blank"
              rel="noopener noreferrer"
              className="w-14 h-14 bg-slate-900 hover:bg-slate-700 rounded-xl flex items-center justify-center transition-colors"
              data-testid="github-link"
            >
              <Github className="w-7 h-7 text-white" />
            </a>
            <a 
              href={portfolioData.personal.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="w-14 h-14 bg-blue-600 hover:bg-blue-500 rounded-xl flex items-center justify-center transition-colors"
              data-testid="linkedin-link"
            >
              <Linkedin className="w-7 h-7 text-white" />
            </a>
            <a 
              href={`mailto:${portfolioData.personal.email}`}
              className="w-14 h-14 bg-rose-500 hover:bg-rose-400 rounded-xl flex items-center justify-center transition-colors"
              data-testid="email-link"
            >
              <Mail className="w-7 h-7 text-white" />
            </a>
          </div>
          
          <a
            href="https://andromechanic.github.io"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-sky-600 hover:text-sky-700 font-semibold"
          >
            Visit Full Portfolio <ExternalLink className="w-4 h-4" />
          </a>
        </div>
        
        {/* Action buttons */}
        <div className="flex gap-4">
          <button
            onClick={onMainMenu}
            className="flex-1 py-3 px-6 bg-white/20 hover:bg-white/30 text-white font-bold rounded-xl flex items-center justify-center gap-2 transition-colors"
            data-testid="main-menu-btn"
          >
            <Home className="w-5 h-5" />
            Menu
          </button>
          <button
            onClick={onRestart}
            className="flex-1 py-3 px-6 bg-gradient-to-r from-yellow-400 to-amber-500 hover:from-yellow-500 hover:to-amber-600 text-slate-900 font-bold rounded-xl flex items-center justify-center gap-2 transition-colors"
            data-testid="play-again-btn"
          >
            <RotateCcw className="w-5 h-5" />
            Play Again
          </button>
        </div>
      </div>
    </div>
  );
};

export default GameComplete;
